/**
 * L'avviso "biglietti comprati sul sito": pallino sulla voce Eventi, suono,
 * notifica. Qui le regole (pure, provate dai test); il controllo ogni minuto
 * sta in hooks/useBigliettiAlerts.ts.
 *
 * Un ordine conta come "arrivato" quando Pixel ha collegato chi ha pagato
 * (ordini_sito.collegato_at): e' pagato, ha biglietti e l'email
 * "Registra i partecipanti" e' partita o sta per partire.
 *
 * Il ricordo di cosa si e' gia' visto e avvisato sta nel browser
 * (localStorage), come per le bozze delle fatture: riaprire l'app non fa
 * risuonare, ma un ordine arrivato ad app chiusa si vede alla riapertura.
 */

export interface OrdineArrivato {
  id: number;
  numero: string;
  acquirente: string;
  biglietti: number;
  eventoId: string | null;
  collegatoAt: string;
}

export interface StatoAvvisi {
  /** Gli ordini arrivati dopo questo istante fanno il pallino. */
  vistoFino: string;
  /** Gli ordini arrivati dopo questo istante non hanno ancora suonato. */
  avvisatoFino: string;
}

export const CHIAVE_AVVISI_BIGLIETTI = 'pixel-slab-biglietti-avvisi';
/** Lo lancia la scheda Biglietti quando si apre: il pallino si azzera subito. */
export const EVENTO_BIGLIETTI_VISTI = 'pixel:biglietti-visti';

/**
 * La prima volta in un browser si guarda indietro di un giorno: un ordine
 * arrivato oggi si vede, lo storico di settimane no.
 */
const GUARDA_INDIETRO_MS = 24 * 3600_000;

export function statoIniziale(adesso: Date): StatoAvvisi {
  const da = new Date(adesso.getTime() - GUARDA_INDIETRO_MS).toISOString();
  return { vistoFino: da, avvisatoFino: da };
}

const istanteValido = (v: unknown): v is string => typeof v === 'string' && Number.isFinite(Date.parse(v));

/** Lo stato salvato, o quello iniziale se manca o e' rovinato. */
export function leggiStato(salvato: string | null, adesso: Date): StatoAvvisi {
  try {
    const s = JSON.parse(salvato ?? '') as Partial<StatoAvvisi>;
    if (istanteValido(s.vistoFino) && istanteValido(s.avvisatoFino)) {
      return { vistoFino: s.vistoFino, avvisatoFino: s.avvisatoFino };
    }
  } catch {
    /* niente di salvato: si parte da capo */
  }
  return statoIniziale(adesso);
}

const dopo = (a: string, b: string) => Date.parse(a) > Date.parse(b);

/** Gli ordini che non hanno ancora suonato, e lo stato da salvare dopo l'avviso. */
export function daAvvisare(ordini: OrdineArrivato[], stato: StatoAvvisi): { nuovi: OrdineArrivato[]; stato: StatoAvvisi } {
  const nuovi = ordini.filter((o) => dopo(o.collegatoAt, stato.avvisatoFino));
  if (nuovi.length === 0) return { nuovi, stato };
  const ultimo = nuovi.reduce((max, o) => (dopo(o.collegatoAt, max) ? o.collegatoAt : max), stato.avvisatoFino);
  return { nuovi, stato: { ...stato, avvisatoFino: ultimo } };
}

/** Aperta la scheda Biglietti: tutto visto, e quello che c'era non suona piu'. */
export function segnaVisti(stato: StatoAvvisi, adesso: Date): StatoAvvisi {
  const ora = adesso.toISOString();
  return { vistoFino: ora, avvisatoFino: dopo(stato.avvisatoFino, ora) ? stato.avvisatoFino : ora };
}

/** Il testo dell'avviso: un ordine con il nome, piu' ordini con il totale dei biglietti. */
export function testoAvviso(nuovi: OrdineArrivato[]): string {
  if (nuovi.length === 1) {
    const o = nuovi[0];
    const quanti = o.biglietti === 1 ? '1 biglietto' : `${o.biglietti} biglietti`;
    return `${o.acquirente} ha comprato ${quanti} (ordine n. ${o.numero})`;
  }
  const biglietti = nuovi.reduce((somma, o) => somma + o.biglietti, 0);
  return `${nuovi.length} nuovi ordini, ${biglietti} biglietti in tutto`;
}

// ---------------------------------------------------------------------------
// Il ricordo nel browser
// ---------------------------------------------------------------------------

export function caricaStato(adesso: Date = new Date()): StatoAvvisi {
  try {
    return leggiStato(window.localStorage.getItem(CHIAVE_AVVISI_BIGLIETTI), adesso);
  } catch {
    return statoIniziale(adesso);
  }
}

export function salvaStato(stato: StatoAvvisi): void {
  try {
    window.localStorage.setItem(CHIAVE_AVVISI_BIGLIETTI, JSON.stringify(stato));
  } catch {
    /* storage non disponibile: si risuonera' alla riapertura, non e' grave */
  }
}

/** Da chiamare quando si guardano i biglietti: azzera il pallino in tutte le schede aperte. */
export function segnaBigliettiVisti(): void {
  salvaStato(segnaVisti(caricaStato(), new Date()));
  window.dispatchEvent(new Event(EVENTO_BIGLIETTI_VISTI));
}
